import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard,
  UserCircle,
  Calendar,
  Sparkles,
  FileText,
  CreditCard,
  Users,
  ShoppingBag,
  Receipt,
  FileDown,
  FileCheck,
  Star,
  MessageCircle,
  MessagesSquare,
  UserPlus,
  Menu,
  X,
  Globe,
  BookOpen,
  Megaphone,
  Share2,
} from 'lucide-react'
import { useUIStore } from '@/store/uiStore'
import { UserMenu } from '@/components/shared/UserMenu'
import { MobileNav, type NavSection } from '@/components/layout/MobileNav'
import { OnboardingChecklist } from '@/components/operator/OnboardingChecklist'
import { ROUTES } from '@/constants/routes'
import { webUrl } from '@/constants/site'
import { cn } from '@/lib/utils'
import { useUnreadMessages, useUnreadPeerMessages } from '@/hooks/useUnreadMessages'
import { usePendingAppointments } from '@/hooks/usePendingAppointments'
import { useUnreadAnnouncements } from '@/hooks/useUnreadAnnouncements'
import { getMyProfile, getOnboardingStatus } from '@/services/operatorService'

const baseSections: NavSection[] = [
  {
    items: [
      { label: 'Dashboard',    icon: LayoutDashboard, to: ROUTES.OPERATOR.DASHBOARD },
    ],
  },
  {
    label: 'Attività',
    items: [
      { label: 'Calendario',     icon: Calendar,      to: ROUTES.OPERATOR.CALENDAR },
      { label: 'Messaggi',       icon: MessageCircle, to: ROUTES.OPERATOR.MESSAGES },
      { label: 'Colleghi e supporto', icon: MessagesSquare, to: ROUTES.OPERATOR.COLLEAGUES },
      { label: 'Clienti',        icon: Users,         to: ROUTES.OPERATOR.CLIENTS },
      { label: 'Storico ordini', icon: ShoppingBag,   to: ROUTES.OPERATOR.ORDERS },
    ],
  },
  {
    label: 'Vetrina',
    items: [
      { label: 'Profilo',    icon: UserCircle, to: ROUTES.OPERATOR.EDIT },
      { label: 'Servizi',    icon: Sparkles,   to: ROUTES.OPERATOR.SERVICES },
      { label: 'Articoli',   icon: FileText,   to: ROUTES.OPERATOR.ARTICLES },
      { label: 'Recensioni', icon: Star,       to: ROUTES.OPERATOR.REVIEWS },
      { label: 'Condividi e badge', icon: Share2, to: ROUTES.OPERATOR.BADGE },
    ],
  },
  {
    label: 'Amministrazione',
    items: [
      { label: 'Fatture',          icon: Receipt,    to: ROUTES.OPERATOR.INVOICES },
      { label: 'Fatture ricevute', icon: FileDown,   to: ROUTES.OPERATOR.PASSIVE_INVOICES },
      { label: 'Dati fiscali',     icon: FileCheck,  to: ROUTES.OPERATOR.FISCAL_DATA },
      { label: 'Abbonamento',      icon: CreditCard, to: ROUTES.OPERATOR.BILLING },
    ],
  },
  {
    items: [
      { label: 'Annunci',           icon: Megaphone, to: ROUTES.OPERATOR.ANNOUNCEMENTS },
      { label: 'Guida',             icon: BookOpen,  to: ROUTES.OPERATOR.GUIDE },
      { label: 'Invita un collega', icon: UserPlus,  to: ROUTES.OPERATOR.REFERRALS },
    ],
  },
]

export function OperatorLayout() {
  const { sidebarOpen, toggleSidebar } = useUIStore()
  const unread  = useUnreadMessages()
  const unreadPeer = useUnreadPeerMessages()
  const pending = usePendingAppointments()
  const unreadAnnouncements = useUnreadAnnouncements()

  // Durante l'onboarding (pre-PUBLISHED) mostriamo solo Dashboard nella sidebar.
  // Operatori legacy senza `onboardingStep` (flusso pre-M2) vedono tutto, come prima.
  const { data: profile } = useQuery({
    queryKey: ['operator', 'profile', 'me'],
    queryFn:  getMyProfile,
    retry: false,
  })
  // Mostriamo il menù completo SOLO a operatori legacy (profilo esistente senza
  // `onboardingStep`, flusso pre-M2) o già pubblicati. In tutti gli altri casi
  // — profilo 404 (operatore appena invitato), profilo in caricamento, o step
  // pre-PUBLISHED — restringiamo a sola Dashboard, coerente con OnboardingGate.
  const onboardingStep = profile?.onboardingStep
  const isLegacy    = Boolean(profile && !onboardingStep)
  const isPublished = onboardingStep === 'PUBLISHED'
  const isOnboarding = !(isLegacy || isPublished)

  // Mini-checklist laterale: visibile su tutte le rotte operatore mentre
  // l'utente è in fase di onboarding, escluse Dashboard e Calendario
  // (la Dashboard ha già la versione full-width inline; il Calendario non è
  // raggiungibile in onboarding, ma proteggiamo comunque).
  const location = useLocation()
  const isDashboardRoute = location.pathname.startsWith(ROUTES.OPERATOR.DASHBOARD)
  const showSideChecklist = isOnboarding && !isDashboardRoute

  const onboardingQ = useQuery({
    queryKey: ['operator', 'onboarding-status'],
    queryFn:  getOnboardingStatus,
    enabled:  showSideChecklist,
    refetchInterval: 60_000,
  })

  const navSections: NavSection[] = baseSections
    .map((section) => ({
      ...section,
      items: section.items
        // In onboarding restringiamo la sidebar a Dashboard + Guida + Annunci
        // + Colleghi e supporto (la guida spiega anche il percorso di
        // attivazione; gli annunci audience=ALL riguardano anche chi si sta
        // attivando; il supporto serve proprio a chi è fermo), coerente con
        // OnboardingGate.
        .filter((item) =>
          !isOnboarding ||
          item.to === ROUTES.OPERATOR.DASHBOARD ||
          item.to === ROUTES.OPERATOR.GUIDE ||
          item.to === ROUTES.OPERATOR.ANNOUNCEMENTS ||
          item.to === ROUTES.OPERATOR.COLLEAGUES
        )
        .map((item) => {
          if (item.to === ROUTES.OPERATOR.MESSAGES) return { ...item, badge: unread }
          if (item.to === ROUTES.OPERATOR.COLLEAGUES) return { ...item, badge: unreadPeer }
          if (item.to === ROUTES.OPERATOR.CALENDAR) return { ...item, badge: pending }
          if (item.to === ROUTES.OPERATOR.ANNOUNCEMENTS) return { ...item, badge: unreadAnnouncements }
          return item
        }),
    }))
    .filter((section) => section.items.length > 0)

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-gray-50 md:flex-row">

      {/* ── Topbar + drawer mobile (< md) ───────────────── */}
      <MobileNav sections={navSections} />

      {/* ── Sidebar (≥ md) ──────────────────────────────── */}
      <aside
        className={cn(
          'hidden flex-col border-r border-gray-200 bg-white transition-all duration-300 md:flex',
          sidebarOpen ? 'w-60' : 'w-16'
        )}
      >
        {/* Logo + toggle */}
        <div className="flex h-16 items-center justify-between border-b px-4">
          {sidebarOpen && (
            <a href={webUrl('/')} className="font-semibold text-holly-800 hover:text-holly-600">
              OLLYlife
            </a>
          )}
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Link al sito pubblico */}
        <a
          href={webUrl('/')}
          title="Vai al sito pubblico"
          className={cn(
            'mx-2 mt-2 flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:border-holly-300 hover:text-holly-700',
            !sidebarOpen && 'justify-center px-2'
          )}
        >
          <Globe className="h-4 w-4 flex-shrink-0" />
          {sidebarOpen && <span>Vai al sito</span>}
        </a>

        {/* Nav links — scrollabile su viewport bassi */}
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2 py-3">
          {navSections.map((section, si) => (
            <div key={section.label ?? `section-${si}`}>
              {si > 0 && (
                sidebarOpen && section.label ? (
                  <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    {section.label}
                  </div>
                ) : (
                  <div className="mx-3 my-2 border-t border-gray-100" />
                )
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    title={item.label}
                    className={({ isActive }) =>
                      cn(
                        'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-holly-50 text-holly-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                        !sidebarOpen && 'justify-center'
                      )
                    }
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {sidebarOpen && <span>{item.label}</span>}
                    {item.badge && item.badge > 0 ? (
                      <span className={cn(
                        'ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-holly-600 px-1.5 text-xs font-bold text-white',
                        !sidebarOpen && 'absolute right-1 top-1 ml-0'
                      )}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    ) : null}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User menu at bottom */}
        <div className={cn('border-t p-3', !sidebarOpen && 'flex justify-center')}>
          <UserMenu compact={!sidebarOpen} />
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────── */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className={cn(
          'mx-auto px-4 py-4 sm:px-6 sm:py-8',
          showSideChecklist ? 'max-w-7xl lg:flex lg:gap-6' : 'max-w-5xl',
        )}>
          {/* Contenuto pagina */}
          <div className={cn(showSideChecklist && 'min-w-0 lg:flex-1')}>
            <Outlet />
          </div>

          {/* Mini-checklist persistente (solo durante onboarding, desktop) */}
          {showSideChecklist && (
            <aside className="mt-8 hidden lg:mt-0 lg:block lg:w-80 lg:flex-shrink-0">
              <div className="sticky top-8">
                {onboardingQ.data ? (
                  <OnboardingChecklist data={onboardingQ.data} />
                ) : (
                  <div className="rounded-2xl border border-gray-100 bg-white p-6 text-xs text-gray-400">
                    Caricamento stato profilo…
                  </div>
                )}
              </div>
            </aside>
          )}
        </div>
      </main>
    </div>
  )
}
